import React, {useState, useMemo} from 'react';
import clsx from 'clsx';
import {flatMap, groupBy, isEmpty, keys, sortBy, values} from "lodash";
import {pluralize} from "../../../lib/TextHelpers";
import Modal from "../../../Modals/Modal";

/**
 *
 * @param productList
 * @param allProductSpecs
 * @returns {JSX.Element|null}
 * @constructor
 */
export default function ProductSpecsDisplay({
    productList = [], allProductSpecs = []
  }
) {
  const [open, setOpen] = useState(false);

  const specsByProduct = useMemo(() => {
    return groupBy(
      flatMap(allProductSpecs, 'specs'),
      'productId'
    );
  }, [allProductSpecs]);

  const toggleOpen = () => setOpen(vis => !vis);

  if (isEmpty(productList) || isEmpty(allProductSpecs))
    return null;

  const productCount = productList.length;
  const firstItem = productList[0];
  if (isEmpty(specsByProduct[firstItem.id]))
    return null;

  return <div>
    <div>
      {(productCount > 1) && <span>
        {pluralize(productCount, 'product matches', 'products match')}
        {' | '}
        </span>}

      <a onClick={toggleOpen} className="text-brand-primary underline cursor-pointer">
        <i className="fa fa-table mr-1" />
        Show Specifications
      </a>
    </div>

    {
      open && <Modal handleClose={toggleOpen}>
        <div>
          <h2 className="text-xl text-gray-800 font-bold">
            Product Specifications
          </h2>

          <MultiItemSpecsTable productList={productList}
            allProductSpecs={allProductSpecs}/>
        </div>
      </Modal>
    }
  </div>

}

function MultiItemSpecsTable({productList, allProductSpecs}) {
  let cols = 1 + productList.length;
  let enableScroll = cols >= 5;

  return <div className={enableScroll ? "overflow-x-auto" : ""}>
    <table className="min-w-max text-sm divide-y divide-x divide-gray-200 text-gray-800">
      <MultiItemSpecHeader productList={productList}/>
      <tbody>
      {allProductSpecs.map(label => <MultiItemSpecRow
        key={label.name}
        label={label} productList={productList}/>)}
      </tbody>
    </table>
  </div>
}

function MultiItemSpecHeader({productList}) {
  let cols = 1 + productList.length;
  let style = {width: `${100 / cols}%`};

  return <thead>
  <tr>
    <th className="font-bold" style={style}> Product SKU:</th>
    {productList.map(product => <th key={product.id} style={style}>{product.sku}</th>)}
  </tr>
  </thead>
}

function MultiItemSpecRow({label, productList}) {
  const groupedSpecs = groupBy(label.specs, s => +s.productId);

  return <tr>
    <td className="px-1 py-1 font-bold">{label.displayName || label.name}</td>
    {productList.map((product, i) => <MultiItemSpecCell key={product.id}
      index={i + 1} product={product} specs={groupedSpecs[+product.id]}/>)}
  </tr>
}

function MultiItemSpecCell({product, specs, index = 0}) {
  const values = (specs || []).map(s => s.value);
  const className = clsx(['px-1 py-1',
    ((index || 0) % 2 === 0) ? "bg-gray-100" : ""]);

  return <td className={className}>{values.join("; ")}</td>
}
